/**
 * PWA installer — registers the service worker, exposes a helper
 * to surface the install prompt, and a thin Push API wrapper.
 *
 * Call `registerServiceWorker()` once from `main.jsx` after React
 * mounts so the SW lifecycle runs alongside the app's. Everything
 * else is opt-in:
 *
 *   - `subscribePushManager(vapidPublicKey)` — gated behind a
 *     UI prompt; only invoked after the user clicks "Enable push
 *     notifications".
 *   - `unsubscribePushManager()` — symmetric teardown.
 *
 * Why not load this from a registered Vite plugin (e.g.
 * `vite-plugin-pwa`)? Because the doc's strict offline-policy
 * needs the hand-tuned shell SW (see `public/service-worker.js`
 * for the rationale); pulling in a generator would either replace
 * our SW or fight it.
 */

/** Register the service worker. Safe to call repeatedly — the
 *  browser dedupes by scope. Returns the registration so callers
 *  that need it (e.g. push-subscribe) can chain off. */
export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }
  // Skip the SW in dev — hot-module-replacement and the SW's
  // cache layer fight each other and the result is stale code
  // hanging around for a refresh cycle. Production builds
  // (NODE_ENV === 'production' bundled by Vite) get the real one.
  if (import.meta.env?.DEV) return null

  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
    })

    // ── Auto-refresh open tabs when a new build takes control ──────
    //
    // Our SW `skipWaiting()`s + `clients.claim()`s, so a deploy that
    // ships a changed service-worker.js activates immediately and
    // becomes the controller of already-open tabs. But those tabs are
    // still showing the OLD index.html + assets until they reload — and
    // if the user navigates first, they hit the stale-bundle #130 crash.
    // Reload once on `controllerchange` so the fresh build is picked up
    // automatically before that can happen.
    //
    // `hadController` guards the FIRST-EVER install: on a fresh visit
    // there is no controller yet, `clients.claim()` fires one
    // controllerchange, and reloading there would be a pointless flash.
    // We only auto-reload when REPLACING an existing controller — i.e. a
    // genuine update. `refreshing` stops a double reload.
    const hadController = Boolean(navigator.serviceWorker.controller)
    let refreshing = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController || refreshing) return
      refreshing = true
      window.location.reload()
    })

    // Long-lived tabs (app left open all day) will not re-check the SW
    // on their own. Poll hourly so a deploy reaches them within the hour
    // instead of "whenever they next happen to navigate".
    setInterval(
      () => {
        reg.update().catch(() => {})
      },
      60 * 60 * 1000,
    )

    return reg
  } catch (err) {
    // Failing the SW register isn't fatal — the site just falls
    // back to plain network. Log to the console for the dev tools.
    // eslint-disable-next-line no-console
    console.warn('Service worker registration failed', err)
    return null
  }
}

// ─── Install prompt ────────────────────────────────────────────────

let deferredPrompt = null

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stash the event so the UI can present an "Install TaskMagix"
    // button at the right moment instead of immediately. The
    // browser-default banner is suppressed by calling
    // `preventDefault` here.
    e.preventDefault()
    deferredPrompt = e
  })
}

/** Returns true when the browser is ready to show our custom
 *  install prompt. The UI can use this to conditionally render
 *  an "Install" button on a relevant screen. */
export function canPromptInstall() {
  return Boolean(deferredPrompt)
}

/** Shows the install prompt. Returns the outcome the user chose
 *  (`'accepted' | 'dismissed'`). */
export async function showInstallPrompt() {
  if (!deferredPrompt) return null
  deferredPrompt.prompt()
  const choice = await deferredPrompt.userChoice
  deferredPrompt = null
  return choice?.outcome ?? null
}

// ─── Push notifications ────────────────────────────────────────────

/** Decode a URL-safe base64 VAPID public key into the Uint8Array
 *  the Push API expects. Verbatim from the standard MDN snippet. */
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

/**
 * Ask the browser for a PushManager subscription. Returns the
 * raw `PushSubscription` (which the server stores in its
 * `push_subscriptions` table — out of scope here). Caller is
 * responsible for prompting the user first; this function
 * assumes consent has been given.
 */
export async function subscribePushManager(vapidPublicKey) {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    return null
  }
  const reg = await navigator.serviceWorker.ready
  const existing = await reg.pushManager.getSubscription()
  if (existing) return existing

  if (!vapidPublicKey) return null
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  })
}

/** Tear down the active push subscription (if any). */
export async function unsubscribePushManager() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false
  }
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return false
  return sub.unsubscribe()
}
